import "./SimSelection.scss";

import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IconTile } from "@/apps/partner/components/IconTile/IconTile";
import { SimVerification } from "@/apps/partner/components/SimVerification/SimVerification";
import { ESim } from "@/assets/icons/icons";
import SimCard from "@/assets/icons/SimCard.svg?react";
import { Step } from "@/components/Onboarding/Steps/Step";

/**
 * This is the first step of the onboarding.
 * It is used to check whether the customer wants to onboard a physical SIM (later called Tripple SIM)
 * or an eSIM. Both have their own specialities, which we will describe later.
 **/
export const SimSelection = () => {
  const { t } = useTranslation();
  const {
    resetField,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const [simVerification, setSimVerification] = useState(false);
  const [selectedSimType, setSelectedSimType] = useState<
    "TRIPLESIM" | "ESIM" | null
  >(null);

  // Watch for form data changes to sync with existing data
  const formSimType = watch("simType");

  // Sync local state with existing form data
  useEffect(() => {
    if (formSimType && !selectedSimType) {
      setSelectedSimType(formSimType);
      if (formSimType === "TRIPLESIM") {
        setSimVerification(true);
      }
    }
  }, [formSimType, selectedSimType]);

  const handleSimTypeSelection = (simType: "TRIPLESIM" | "ESIM") => {
    setValue("simType", simType, { shouldValidate: false });
    setSelectedSimType(simType);

    if (simType === "TRIPLESIM") {
      setSimVerification(true);
    } else {
      setSimVerification(false);
      resetField("iccid");
      resetField("puk");
    }
  };

  return (
    <>
      <Step
        id="partner-select-sim"
        data-testid="select-sim"
        headline={t("partnerPortal:addDevice.step1.title")}
        subheadline={t("partnerPortal:addDevice.step1.description")}
      >
        <div id="add-device" className="mt-10">
          {errors.simType && !selectedSimType && (
            <span className="mt-2 flex justify-center text-sm text-error-100">
              {String(errors.simType.message)}
            </span>
          )}
          <div className="flex flex-col gap-4 md:flex-row md:gap-8">
            <IconTile
              label={t("Tile.label.activateSim")}
              icon={<SimCard />}
              selected={selectedSimType === "TRIPLESIM"}
              onClick={() => handleSimTypeSelection("TRIPLESIM")}
            />
            <IconTile
              dataTestId="esim-tile"
              label={t("Tile.label.activateESim") + " *"}
              icon={<ESim />}
              selected={selectedSimType === "ESIM"}
              onClick={() => handleSimTypeSelection("ESIM")}
            />
          </div>
        </div>
      </Step>
      <div className="flex justify-start text-xs text-secondary-100 md:ml-[calc(50%+8px)]">
        *
        <span>
          {t("Onboarding.sections.rate-selection.mobileHint").split("<0>")[0]}
          <a
            className="underline"
            href={t("Onboarding.sections.rate-selection.mobileHintLink")}
            target="_blank"
            rel="noreferrer"
          >
            {
              t("Onboarding.sections.rate-selection.mobileHint")
                .split("<0>")[1]
                .split("</0>")[0]
            }
          </a>
          {t("Onboarding.sections.rate-selection.mobileHint").split("</0>")[1]}
        </span>
      </div>

      <div>{simVerification && <SimVerification />}</div>
    </>
  );
};
